import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet } from "lucide-react";
import { api, errMsg, dt, gbp } from "@/lib/api";
import { PageHeader, Panel, StageBadge } from "@/components/tam/ui";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function Rollback({ b, onDone }) {
  const run = async () => {
    try {
      const { data } = await api.delete(`/uploads/${b.id}`);
      toast.success(`Rolled back – ${data.removed} untouched accounts removed`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`rollback-btn-${b.id}`}>Roll back</Button></AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Roll back this upload?</AlertDialogTitle>
          <AlertDialogDescription>Accounts from this batch with no logged activity will be removed. Worked accounts, notes and the audit trail are always kept.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="rollback-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} className="bg-[#D9232A] hover:bg-[#B81920]" data-testid="rollback-confirm">Roll back</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Uploads() {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(null);
  const ref = useRef();
  const load = useCallback(() => api.get("/uploads").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setProgress(5);
    try {
      const { data } = await api.post("/uploads", fd, { onUploadProgress: (e) => setProgress(Math.min(90, Math.round((e.loaded / e.total) * 90))) });
      setProgress(100);
      toast.success(`Imported ${data.accounts} accounts (${data.created} new, ${data.updated} updated)`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setTimeout(() => setProgress(null), 800);
      ref.current.value = "";
    }
  };

  return (
    <div data-testid="uploads-page">
      <PageHeader title="Upload management" subtitle="Import the monthly Lost & Non Trading workbook. Existing accounts keep their contacts, notes and progress." />
      <Panel className="mb-6">
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
          className="border-2 border-dashed border-slate-300 rounded-md p-10 flex flex-col items-center text-center hover:border-[#D9232A] transition-colors" data-testid="upload-dropzone">
          <UploadCloud size={36} className="text-[#D9232A]" />
          <p className="font-display font-semibold mt-3">Drop the .xlsx workbook here</p>
          <p className="text-xs text-slate-500 mt-1">One sheet per account manager · columns: Acc No, Name, Rep, Price Band, yearly Value/Qty</p>
          <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="upload-file-input" />
          <Button className="mt-5 bg-[#0B132B] hover:bg-[#1c2647]" onClick={() => ref.current.click()} disabled={progress !== null} data-testid="upload-select-btn">Choose file</Button>
          {progress !== null && <div className="w-full max-w-sm mt-5"><Progress value={progress} data-testid="upload-progress" /><p className="text-xs text-slate-500 mt-2">{progress < 90 ? "Uploading…" : "Processing accounts…"}</p></div>}
        </div>
      </Panel>
      <Panel title="Upload history" testId="upload-history">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="text-xs uppercase text-slate-500 text-left"><tr><th className="pb-2">File</th><th className="pb-2">Uploaded</th><th className="pb-2 text-right">Accounts</th><th className="pb-2 text-right">New / Updated</th><th className="pb-2 text-right">Lines</th><th className="pb-2 text-right">Value</th><th className="pb-2">Status</th><th /></tr></thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-t border-slate-100" data-testid={`upload-row-${b.id}`}>
                  <td className="py-3"><span className="flex items-center gap-2"><FileSpreadsheet size={15} className="text-emerald-700" />{b.filename}</span><p className="text-xs text-slate-500">{b.sheets.length} sheets</p></td>
                  <td className="py-3 text-xs">{b.uploaded_by}<br /><span className="text-slate-500">{dt(b.uploaded_at)}</span></td>
                  <td className="py-3 text-right font-mono">{b.accounts}</td>
                  <td className="py-3 text-right font-mono">{b.created} / {b.updated}</td>
                  <td className="py-3 text-right font-mono">{b.product_lines}</td>
                  <td className="py-3 text-right font-mono">{gbp(b.total_value)}</td>
                  <td className="py-3"><StageBadge stage={b.status === "Completed" ? "Completed" : "Not Interested"} />
                    {b.rolled_back_by && <p className="text-xs text-slate-500 mt-1">{b.status} by {b.rolled_back_by}</p>}</td>
                  <td className="py-3 text-right">{b.status === "Completed" && <Rollback b={b} onDone={load} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
